/**
 * HierarchicalOrgSelect
 * Trädvy-dropdown för organisationer med expanderbara mappar.
 * Använder GET /api/organizations/tree – begränsat till användarens org och underträd.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FolderIcon,
  FolderOpenIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { FolderIcon as FolderIconSolid } from '@heroicons/react/24/solid';
import { useTranslation } from '@/store/languageStore';
import { useOrganizationTree, type OrgTreeNode } from '@/hooks/useOrganizationTree';

const normalize = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

interface HierarchicalOrgSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  excludeIds?: string[];
  showEmptyOption?: boolean;
  disabled?: boolean;
  className?: string;
}

interface OrgNode {
  id: string;
  name: string;
  level: number;
  hasChildren: boolean;
  parentId?: string;
}

function findOrgInTree(orgs: OrgTreeNode[], id: string): OrgTreeNode | null {
  for (const org of orgs) {
    if (String(org.id) === id) return org;
    if (org.children?.length) {
      const found = findOrgInTree(org.children, id);
      if (found) return found;
    }
  }
  return null;
}

function flattenTree(
  nodes: OrgTreeNode[],
  level = 0,
  parentId?: string
): OrgNode[] {
  const result: OrgNode[] = [];
  for (const node of nodes) {
    const children = node.children ?? [];
    result.push({
      id: String(node.id),
      name: node.name,
      level,
      hasChildren: children.length > 0,
      parentId,
    });
    if (children.length > 0) {
      result.push(...flattenTree(children, level + 1, String(node.id)));
    }
  }
  return result;
}

const HierarchicalOrgSelect: React.FC<HierarchicalOrgSelectProps> = ({
  value,
  onChange,
  placeholder,
  emptyLabel,
  excludeIds,
  showEmptyOption = true,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const { tree, loading } = useOrganizationTree();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const flatList = useMemo(() => flattenTree(tree), [tree]);

  const filteredOrgs = useMemo(() => {
    let result = flatList;

    if (excludeIds && excludeIds.length > 0) {
      const excludeSet = new Set(excludeIds);
      result = result.filter((org) => !excludeSet.has(org.id));
    }

    if (!search.trim()) return result;

    const searchNorm = normalize(search);
    return result.filter((org) => normalize(org.name).includes(searchNorm));
  }, [flatList, search, excludeIds]);

  const isVisible = useCallback(
    (org: OrgNode): boolean => {
      if (search.trim()) return true;
      if (org.level === 0) return true;

      let parentId = org.parentId;
      while (parentId) {
        if (!expanded.has(parentId)) return false;
        const parent = flatList.find((o) => o.id === parentId);
        parentId = parent?.parentId;
      }
      return true;
    },
    [expanded, flatList, search]
  );

  const selectedOrg = useMemo(() => {
    if (!value) return null;
    return findOrgInTree(tree, value);
  }, [tree, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const toggleExpand = (e: React.MouseEvent, orgId: string) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  const handleSelect = (orgId: string) => {
    onChange(orgId);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  const renderOrgNode = (org: OrgNode) => {
    const isExpanded = expanded.has(org.id);
    const isSelected = value === org.id;
    const visible = isVisible(org);

    if (!visible) return null;

    return (
      <div
        key={org.id}
        className={`
          w-full px-2 py-1.5 text-left text-sm hover:bg-bgBase transition-colors flex items-center gap-1
          ${isSelected ? 'bg-primary/10 text-primary' : 'text-textDefault'}
        `}
        style={{ paddingLeft: `${8 + org.level * 20}px` }}
      >
        {org.hasChildren ? (
          <button
            type="button"
            onClick={(e) => toggleExpand(e, org.id)}
            className="p-0.5 hover:bg-bgContainer rounded"
            aria-label={isExpanded ? 'Collapse organization' : 'Expand organization'}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-3.5 w-3.5 text-textSecondary" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-textSecondary" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        <button
          type="button"
          onClick={() => handleSelect(org.id)}
          className="min-w-0 flex-1 flex items-center gap-1 text-left"
        >
          {org.hasChildren ? (
            isExpanded ? (
              <FolderOpenIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
            ) : (
              <FolderIconSolid className="h-5 w-5 text-amber-500 flex-shrink-0" />
            )
          ) : (
            <FolderIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
          )}

          <span className={`truncate ${isSelected ? 'font-medium' : ''}`}>{org.name}</span>
        </button>
      </div>
    );
  };

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-1.5 border border-border rounded bg-bgContainer text-textDefault text-sm
          flex items-center justify-between gap-2 min-w-[200px]
          focus:outline-none focus:ring-2 focus:ring-primary
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}
        `}
      >
        <div className="flex items-center gap-2 truncate">
          {selectedOrg && (
            <FolderIconSolid className="h-4 w-4 text-amber-500 flex-shrink-0" />
          )}
          <span className={selectedOrg ? 'text-textDefault truncate' : 'text-textSecondary'}>
            {loading
              ? t('common.loading') || 'Laddar...'
              : selectedOrg
                ? selectedOrg.name
                : placeholder || t('fleet.filterAllOrganizations') || 'Alla organisationer'}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && !disabled && (
            <XMarkIcon
              className="h-4 w-4 text-textSecondary hover:text-textDefault"
              onClick={handleClear}
            />
          )}
          <ChevronDownIcon
            className={`h-4 w-4 text-textSecondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-[9999] mt-1 w-full min-w-[280px] max-w-[400px] bg-bgContainer border border-border rounded-lg shadow-lg">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search') || 'Sök...'}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded bg-bgBase text-textDefault placeholder-textSecondary focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <XMarkIcon className="h-4 w-4 text-textSecondary hover:text-textDefault" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[350px] overflow-y-auto py-1">
            {showEmptyOption && (
              <>
                <button
                  type="button"
                  onClick={() => handleSelect('')}
                  className={`
                    w-full px-3 py-2 text-left text-sm hover:bg-bgBase transition-colors flex items-center gap-2
                    ${!value ? 'bg-primary/10 text-primary font-medium' : 'text-textDefault'}
                  `}
                >
                  <BuildingOfficeIcon className="h-5 w-5 text-textSecondary flex-shrink-0" />
                  {emptyLabel || t('fleet.filterAllOrganizations') || 'Alla organisationer'}
                </button>

                <div className="border-t border-border my-1" />
              </>
            )}

            {filteredOrgs.length === 0 && search && (
              <div className="px-3 py-4 text-sm text-textSecondary text-center">
                {t('common.noResults') || 'Inga resultat'}
              </div>
            )}

            {filteredOrgs.map((org) => renderOrgNode(org))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchicalOrgSelect;
